import { X, Play, Plus, Check } from "lucide-react";
import { useEffect, useState } from "react";
import api from "../lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AlbumArt from "./AlbumArt";

export const MovieDetailModal = ({ movie, open, onClose, onWatchlistChange }) => {
  const nav = useNavigate();
  const [inList, setInList] = useState(false);

  useEffect(() => {
    if (!movie || !open) return;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [movie, open]);

  useEffect(() => {
    if (!movie) return;
    let cancelled = false;
    api.get("/watchlist").then(({ data }) => {
      if (!cancelled) setInList(data.some((m) => m.id === movie.id));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [movie]);

  if (!open || !movie) return null;

  const toggle = async () => {
    try {
      if (inList) {
        await api.delete(`/watchlist/${movie.id}`);
        setInList(false);
        toast.success("Removed from the shelf");
      } else {
        await api.post(`/watchlist/${movie.id}`);
        setInList(true);
        toast.success("Saved to your shelf");
      }
      onWatchlistChange?.();
    } catch {
      toast.error("Could not update list");
    }
  };

  const play = () => {
    onClose?.();
    nav(`/watch/${movie.id}`);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start md:items-center justify-center overflow-y-auto"
      data-testid="movie-detail-modal"
    >
      <div className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={onClose} />
      <div className="relative w-full max-w-4xl mx-4 my-8 bg-[#0A0A0A] border border-[#222] overflow-hidden fade-up">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-black/60 hover:bg-black text-white transition-colors duration-300"
          data-testid="modal-close-button"
          aria-label="Close"
        >
          <X size={18} strokeWidth={1.5} />
        </button>

        <div className="relative h-[280px] md:h-[440px]">
          <AlbumArt url={movie.backdrop_url || movie.poster_url} className="absolute inset-0" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-10">
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-white" data-testid="modal-movie-title">
              {movie.title}
            </h2>
            <div className="flex flex-wrap items-center gap-3 mt-4 text-xs uppercase tracking-[0.2em] text-[#8A8A8A]">
              <span>{movie.year}</span>
              <span>·</span>
              <span>{movie.rating}</span>
              {movie.duration_minutes ? (<><span>·</span><span>{movie.duration_minutes} min</span></>) : null}
            </div>
            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={play}
                className="flex items-center gap-2 bg-[#D9381E] hover:bg-[#ED4B32] text-white px-6 py-3 transition-colors duration-300"
                data-testid="modal-play-button"
              >
                <Play size={16} fill="white" strokeWidth={0} />
                <span className="text-sm uppercase tracking-[0.2em] font-medium">Watch</span>
              </button>
              <button
                onClick={toggle}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-5 py-3 transition-colors duration-300 backdrop-blur border border-white/10"
                data-testid="modal-watchlist-button"
              >
                {inList ? <Check size={16} strokeWidth={1.5} /> : <Plus size={16} strokeWidth={1.5} />}
                <span className="text-xs uppercase tracking-[0.2em]">{inList ? "On the Shelf" : "Save to Shelf"}</span>
              </button>
            </div>
          </div>
        </div>

        <div className="p-8 md:p-10 grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <p className="text-base leading-relaxed text-[#C8C8C8]" data-testid="modal-movie-description">
              {movie.description}
            </p>
          </div>
          <div className="space-y-4 text-sm">
            {movie.cast?.length ? (
              <div>
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A]">Cast</span>
                <p className="mt-1 text-[#C8C8C8]">{movie.cast.join(", ")}</p>
              </div>
            ) : null}
            {movie.director ? (
              <div>
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A]">Director</span>
                <p className="mt-1 text-[#C8C8C8]">{movie.director}</p>
              </div>
            ) : null}
            {movie.genres?.length ? (
              <div>
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A]">Genres</span>
                <p className="mt-1 text-[#C8C8C8]">{movie.genres.join(", ")}</p>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MovieDetailModal;
